// Setup › Your AI tools on the SettingsPage pattern (BL-189): the AI tools (Claude or another MCP client)
// connected with the person's own sign-in (GET /me/ai-tools, DEC-009). As SignInScreen.tsx's AiTools
// draws them in Blueprint, with nothing changed: Connect an AI tool opens a FormSheet where the inline
// form was; the key it makes is shown once, in a Notice, until Done; each tool is a row with its
// standing (acts as you or asks first, until when, last used) and its two actions. Riverstone keeps
// Your sign-in's AI tools part.
import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Button, Checkbox, KeyValue, Link, Notice, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';

interface AiTool {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  actsAsYou: boolean;
}

const LEDE = 'Claude or another AI tool, connected with your own sign-in: it does what you can do.';
const LINE =
  'Connect Claude or another AI tool to Flowology with your own sign-in. It can do what you can do. When it acts as you, it doesn’t ask first, except for money going out and anything that can’t be undone (refunds, voids, write-offs, paying bills, merging customers, closing a month): those always wait for you to confirm here. Approving requests, sign-offs, who may do what and payroll are never done by an AI tool.';
const FOR = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];
const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function AiToolsPage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage head={{ ...(crumb ? { crumb } : {}), title: 'Your AI tools', lede: LEDE }}>
          <AiTools timeZone={timeZone} />
        </SettingsPage>
      </main>
    </div>
  );
}

function AiTools({ timeZone }: { timeZone: string }) {
  const [tools, setTools] = useState<AiTool[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [made, setMade] = useState<{ key: string; actsAsYou: boolean } | null>(null);
  const day = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { dateStyle: 'medium', timeZone });
  const load = useCallback(
    () =>
      client
        .me<{ tools: AiTool[] }>('GET', '/me/ai-tools')
        .then((r) => {
          setTools(r.tools);
          setLoadError(null);
        })
        .catch((e: unknown) => setLoadError(message(e))),
    [],
  );
  useEffect(() => void load(), [load]);
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await f();
      setError(null);
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <SettingsSection
        title="AI tools"
        line={LINE}
        {...(made
          ? {}
          : {
              action: (
                // drawn as Blueprint drew it: the section's way in, an underlined word
                <Link onPress={() => setConnecting(true)} isDisabled={busy}>
                  Connect an AI tool
                </Link>
              ),
            })}
        {...(error ? { note: <span role="alert">{error}</span> } : {})}
        status={tools ? 'ready' : loadError ? 'failed' : 'loading'}
        {...(loadError ? { failedText: loadError } : {})}
        onRetry={() => void load()}
      >
        {made && (
          <Notice
            tone="done"
            takesFocus
            title="Your key. Copy it into the AI tool now: it isn’t shown again."
            actions={
              <>
                <Button onPress={() => void navigator.clipboard?.writeText(made.key)}>Copy the key</Button>
                <Link onPress={() => setMade(null)}>Done</Link>
              </>
            }
          >
            <KeyValue
              label="Your key"
              items={[
                { label: 'Key', value: made.key },
                { label: 'Flowology’s MCP address', value: `${location.origin}/mcp` },
              ]}
            />
            {made.actsAsYou ? 'It acts as you.' : 'It asks you first for what it does.'}
          </Notice>
        )}
        {tools?.map((t) => {
          const name = t.label ?? 'AI tool';
          return (
            <SettingRow
              key={t.id}
              name={name}
              help={`${t.actsAsYou ? 'acts as you' : 'asks you first'}, until ${day(t.expiresAt)}${
                t.lastUsedAt ? `, last used ${day(t.lastUsedAt)}` : ', not used yet'
              }`}
              control={
                <>
                  <Link
                    isDisabled={busy}
                    onPress={() =>
                      void run(() =>
                        client.me('POST', `/me/ai-tools/${t.id}/acts-as-you`, { on: !t.actsAsYou }),
                      )
                    }
                  >
                    {t.actsAsYou ? 'Ask me first instead' : 'Let it act as me'}
                  </Link>{' '}
                  <Link
                    label={`Disconnect ${name}`}
                    isDisabled={busy}
                    onPress={() => void run(() => client.me('POST', `/me/ai-tools/${t.id}/revoke`))}
                  >
                    Disconnect
                  </Link>
                </>
              }
            />
          );
        })}
      </SettingsSection>
      {connecting && (
        <ConnectSheet
          onMade={(m) => {
            setMade(m);
            setError(null);
            void load();
          }}
          onClose={() => setConnecting(false)}
        />
      )}
    </>
  );
}

/** Makes a key for an AI tool (POST /me/ai-tools): its name, whether it acts as you, for how long. */
function ConnectSheet({
  onMade,
  onClose,
}: {
  onMade: (m: { key: string; actsAsYou: boolean }) => void;
  onClose: () => void;
}) {
  const blank = { label: 'Claude', actsAsYou: true, days: '30' };
  const [f, setF] = useState(blank);
  return (
    <FormSheet
      isOpen
      title="Connect an AI tool"
      submitLabel="Connect an AI tool"
      isDirty={JSON.stringify(f) !== JSON.stringify(blank)}
      isSubmitDisabled={!f.label.trim()}
      onClose={onClose}
      onSubmit={async () => {
        try {
          const r = await client.me<{ key: string; actsAsYou: boolean }>('POST', '/me/ai-tools', {
            label: f.label,
            actsAsYou: f.actsAsYou,
            days: Number(f.days),
          });
          onMade({ key: r.key, actsAsYou: r.actsAsYou });
        } catch (e) {
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      <TextField label="Name it" maxLength={60} value={f.label} onChange={(label) => setF({ ...f, label })} />
      <Checkbox isSelected={f.actsAsYou} onChange={(actsAsYou) => setF({ ...f, actsAsYou })}>
        Act as me
      </Checkbox>
      <SelectField label="For" value={f.days} options={FOR} onChange={(days) => setF({ ...f, days })} />
    </FormSheet>
  );
}
